/**
 * MY LOANS (UF-06). The borrower's loan portfolio.
 *
 * Each loan is a card that answers the questions a borrower asks at a glance: what is outstanding, what
 * is the next payment, how far through am I. The detail lives one tap away.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, LipaApiError, type FinanceLoan } from '@/lib/api';
import { PageHeader } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { fmtDateShort } from '@/lib/format';

export default function MyLoans() {
  const [loans, setLoans] = useState<FinanceLoan[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setLoans((await api.finance.loans()).loans);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your loans.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <ErrorState body={error} onRetry={() => void load()} />;
  if (!loans) return <LoadingCard lines={4} />;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Link
        to="/credit"
        className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
      >
        <BackIcon /> Credit
      </Link>

      <PageHeader
        title="My loans"
        subtitle="Your active and past financing."
        actions={
          <Link
            to="/credit/repay"
            className="rounded-field border border-border px-3.5 py-2 text-sm font-medium text-ink transition hover:bg-surface-sunken"
          >
            Repayment center
          </Link>
        }
      />

      {loans.length === 0 ? (
        <EmptyState
          title="No loans yet"
          body="When you accept a financing offer, the loan appears here with its balance, schedule, and collateral."
          action={{ label: 'Browse financing', href: '/credit/marketplace' }}
        />
      ) : (
        <ul className="space-y-3">
          {loans.map((l) => (
            <li key={l.id}>
              <Reveal>
                <Link
                  to={`/credit/loans/${l.id}`}
                  className="block rounded-card border border-border bg-surface p-5 shadow-card transition hover:border-primary/40 hover:shadow-soft"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{l.asset}</p>
                      <p className="text-micro text-ink-subtle">
                        {l.product} · {l.provider}
                      </p>
                    </div>
                    <StatusPill status={l.status} />
                  </div>
                  <div className="mt-3">
                    <p className="text-micro text-ink-subtle">Outstanding</p>
                    <p className="tabular text-2xl font-semibold text-ink">KES {l.outstanding}</p>
                  </div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-sunken">
                    <div
                      className="h-full rounded-full bg-primary"
                      style={{ width: `${l.paidPct}%` }}
                    />
                  </div>
                  <div className="tabular mt-2 flex justify-between text-micro text-ink-subtle">
                    <span>{l.paidPct}% repaid</span>
                    <span>
                      Next: KES {l.monthly} on {fmtDateShort(l.nextDue)}
                    </span>
                  </div>
                </Link>
              </Reveal>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function BackIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M15 6l-6 6 6 6" />
    </svg>
  );
}
